/**
 * The billing section of the booked-shipment page.
 *
 * The section renders the invoices; what lives here is the downloading — one
 * invoice at a time or all of them as a zip — and which download is in flight,
 * so the section can show the right spinner.
 */

import { useShipmentService } from "@/Services/ShipmentService";
import type { Invoice } from "@freightsimple/generated-dashboard-openapi-client/models/Invoice";
import type { Shipment } from "@freightsimple/generated-dashboard-openapi-client/models/Shipment";
import { useState } from "react";
import ViewShipmentsBillingSection from "../CustomComponents/ViewShipmentsBillingSection";

interface ViewShipmentsBillingSectionForShipmentProps {
  shipment: Shipment;
  invoices: Invoice[];
  totalCouponAmount: number;
}

export function ViewShipmentsBillingSectionForShipment(
  props: ViewShipmentsBillingSectionForShipmentProps,
) {
  const { shipment, invoices, totalCouponAmount } = props;
  const { downloadInvoice, downloadInvoicesZip } = useShipmentService();
  const [downloadingAll, setDownloadingAll] = useState(false);
  const [downloadingInvoiceId, setDownloadingInvoiceId] = useState<
    string | undefined
  >(undefined);

  async function handleDownloadInvoice(
    invoiceIdentifier: string,
    key: string,
    invoiceId: string,
  ) {
    setDownloadingInvoiceId(invoiceId);
    try {
      await downloadInvoice(shipment.shipmentId, invoiceIdentifier, key);
    } finally {
      setDownloadingInvoiceId(undefined);
    }
  }

  async function handleDownloadAllInvoices() {
    setDownloadingAll(true);
    try {
      const invoicesData = invoices.map((invoice) => ({
        invoicePdfKey: invoice.invoicePdfKey,
        invoiceIdentifier: invoice.invoiceIdentifier,
      }));
      await downloadInvoicesZip(shipment.shipmentId, invoicesData);
    } finally {
      setDownloadingAll(false);
    }
  }

  return (
    <ViewShipmentsBillingSection
      invoices={invoices}
      totalCouponAmount={totalCouponAmount}
      onDownloadInvoice={function (invoiceIdentifier, key) {
        const invoice = invoices.find(
          (inv) => inv.invoiceIdentifier === invoiceIdentifier,
        );
        if (invoice) {
          handleDownloadInvoice(invoiceIdentifier, key, invoice.invoiceId);
        }
      }}
      onDownloadAllInvoices={handleDownloadAllInvoices}
      downloadingAll={downloadingAll}
      downloadingInvoiceId={downloadingInvoiceId}
    />
  );
}
